export type CopyLocale = 'es' | 'en';
export type LocalizedCopy = Record<CopyLocale, string>;

export interface CopyEntry {
  key: string;
  /** Route path used to load the page in the editor iframe, e.g. '/' or '/suscripcion'. */
  page: string;
  /** Human label for grouping in the editor list. */
  pageLabel: string;
  /** Grouping within a page. */
  section: string;
  default: LocalizedCopy;
  /** True when the string contains {placeholder} tokens that must be preserved. */
  hasVars?: boolean;
}

/** Builds the repetitive per-plan feature-bullet entries for /suscripcion. */
function planFeatureEntries(plan: string, sectionLabel: string, items: [string, string][]): Record<string, CopyEntry> {
  const entries: Record<string, CopyEntry> = {};
  items.forEach(([es, en], i) => {
    const key = `suscripcion.plan.${plan}.feature.${i + 1}`;
    entries[key] = { key, page: '/suscripcion', pageLabel: 'Suscripción', section: sectionLabel, default: { es, en } };
  });
  return entries;
}

export const copyRegistry: Record<string, CopyEntry> = {
  'home.hero.title': {
    key: 'home.hero.title',
    page: '/',
    pageLabel: 'Home',
    section: 'Hero',
    default: {
      es: 'Encuentra el espacio perfecto para tu negocio o proyecto',
      en: 'Find the perfect space for your business or project',
    },
  },
  'home.hero.description': {
    key: 'home.hero.description',
    page: '/',
    pageLabel: 'Home',
    section: 'Hero',
    default: {
      es: 'Conectamos a quienes buscan espacios de trabajo con propietarios que los ofrecen. Oficinas, coworkings, talleres, locales y más, todo en un mismo lugar. Sin intermediarios, proceso simple y seguro.',
      en: 'We connect people looking for workspaces with owners who offer them. Offices, coworkings, workshops, retail spaces and more, all in one place. No intermediaries, simple and secure process.',
    },
  },
  'home.hero.ctaPublish': {
    key: 'home.hero.ctaPublish',
    page: '/',
    pageLabel: 'Home',
    section: 'Hero',
    default: { es: 'Publica tu espacio', en: 'List your space' },
  },
  'home.hero.ctaRequest': {
    key: 'home.hero.ctaRequest',
    page: '/',
    pageLabel: 'Home',
    section: 'Hero',
    default: { es: 'Solicita un espacio', en: 'Request a space' },
  },
  'suscripcion.cta.select': {
    key: 'suscripcion.cta.select',
    page: '/suscripcion',
    pageLabel: 'Suscripción',
    section: 'CTA',
    default: { es: 'Seleccionar', en: 'Select' },
  },
  'suscripcion.cta.contact': {
    key: 'suscripcion.cta.contact',
    page: '/suscripcion',
    pageLabel: 'Suscripción',
    section: 'CTA',
    default: { es: 'Contactar', en: 'Contact us' },
  },
  ...planFeatureEntries('free', 'Plan Free', [
    ['Publicación gratis de 1 espacio', 'Free listing for 1 space'],
    ['Información básica de contacto', 'Basic contact information'],
  ]),
  ...planFeatureEntries('basic', 'Plan Basic', [
    ['PAGO TRIMESTRAL', 'QUARTERLY PAYMENT'],
    ['Alquila tu espacio sin comisión', 'Rent out your space with no commission'],
    ['Membresía activa por 3 meses', 'Active membership for 3 months'],
    ['Información completa de contacto', 'Full contact information'],
    ['Reedición de anuncio', 'Listing re-publication'],
  ]),
  ...planFeatureEntries('premium', 'Plan Premium', [
    ['PAGO ANUAL', 'ANNUAL PAYMENT'],
    ['Publica hasta 3 espacios', 'List up to 3 spaces'],
    ['Membresía activa por 12 meses', 'Active membership for 12 months'],
    ['Alquila tu espacio sin comisión', 'Rent out your space with no commission'],
    ['Información completa de contacto', 'Full contact information'],
    ['Recomendación a usuarios interesados', 'Recommended to interested users'],
    ['Reedición de anuncio', 'Listing re-publication'],
  ]),
  ...planFeatureEntries('management', 'Plan Gestión', [
    ['Todo lo incluido en el plan Premium', 'Everything included in the Premium plan'],
    ['Gestión completa de tu espacio', 'Full management of your space'],
    ['Sesión fotográfica profesional', 'Professional photo session'],
    ['Respuesta a consultas en tu nombre', 'Inquiries answered on your behalf'],
    ['Gestión de agenda y visitas', 'Calendar and visit management'],
    ['Asesoramiento personalizado de precios', 'Personalized pricing advice'],
    ['Gestión de contratos y documentación', 'Contract and documentation management'],
    ['Soporte prioritario 24/7', '24/7 priority support'],
    ['Promoción destacada en redes sociales', 'Featured promotion on social media'],
  ]),
};

export const copyRegistryList: CopyEntry[] = Object.values(copyRegistry);
